import { createBrowserRouter, Outlet, Navigate } from 'react-router-dom';
import RequireAuth from '@/components/common/RequireAuth';
import BaseLayout from '@/components/common/BaseLayout';
import Home from '@/pages/Home';
import Bookings from '@/pages/Bookings/List';
import BookingForm from '@/pages/Bookings/Form';
import BookingEdit from '@/pages/Bookings/Edit';
import ClosedSchedule from '@/pages/ClosedSchedule/List';
import ClosedScheduleForm from '@/pages/ClosedSchedule/Form';
import Holiday from '@/pages/Holiday';
import News from '@/pages/News/List';
import LoginPage from '@/pages/Login';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import Coupon from '@/pages/Coupon';
import Customer from '@/pages/Customers';
import User from '@/pages/Users';
import Role from '@/pages/Roles';
import VenueSummary from '@/pages/Venues/List';
import VenueDetail from '@/pages/Venues/Edit';
import ProgramSummary from '@/pages/Programs/List';
import ProgramDetail from '@/pages/Programs/Edit';
import Refund from '@/pages/Refund';
import Subscription from '@/pages/Subscription/List';
import SubscriptionDetail from '@/pages/Subscription/Form';
import SubscriptionEdit from '@/pages/Subscription/Edit';
import Invoice from '@/pages/Invoice';
import InvoiceDetail from '@/pages/Invoice/Detail';
import IncomeStatement from '@/pages/Report/IncomeStatement';
import BookingInsight from '@/pages/Report/BookingInsight';
import NewsDetail from '@/pages/News/NewsDetail';
import { CustomerRefund } from "@/pages/CustomerRefund";
import Photos from '@/pages/Photos';
import Banners from '@/pages/Banners';
import BookingReschedule from '@/pages/Bookings/Reschedule';
import Clubs from '@/pages/Clubs/List';
import ClubDetail from '@/pages/Clubs/ClubDetail';

const router = createBrowserRouter([
	{
		path: '/',
		element: <Navigate to='/dashboard/home'/>
	},
	{
		path: '/login',
		element:
			localStorage.getItem('access_token')
				? <Navigate to='/dashboard/home'/>
				: <LoginPage />,
	},
	{
		path: '/forgot-password',
		element: <ForgotPassword />,
	},
	{
		path: '/reset-password',
		element: <ResetPassword />,
	},
	{
		path: '/dashboard/*',
		element: (<RequireAuth access="Dashboard"><BaseLayout /></RequireAuth>),
		handle: { crumb: 'Dashboard', isClickable: false },
		children: [
			{
				path: 'not-authorized',
				element: (
					<div className="flex flex-col justify-center items-center w-full h-full">
						<p className="font-bold text-2xl">Not Authorized</p>
						<p className="font-medium">Try again with another account or contact your superior for access</p>
					</div>)
			},
			{
				path: 'home',
				element: (
					<RequireAuth access="Dashboard">
						<Home />
					</RequireAuth>
				),
			},
			{
				path: 'booking',
				element: (
					<RequireAuth access="Booking">
						<Bookings />
					</RequireAuth>
				),
				handle: { crumb: 'Bookings', isClickable: false },
			},
			{
				path: 'booking/add',
				element: (
					<RequireAuth access="Booking">
						<BookingForm />
					</RequireAuth>
				),
			},
			{
				path: 'booking/:id/edit',
				element: (
					<RequireAuth access="Booking">
						<BookingEdit />
					</RequireAuth>
				),
			},
			{
				path: 'booking/:id/reschedule',
				element: (
					<RequireAuth access="Booking">
						<BookingReschedule />
					</RequireAuth>
				),
			},
			{
				path: 'news',
				element: (
					<RequireAuth access="News">
						<News />
					</RequireAuth>
				),
			},
			{
				path: 'news/add',
				element: (
					<RequireAuth access="News">
						<NewsDetail />
					</RequireAuth>
				),
			},
			{
				path: 'news/:newsId/edit',
				element: (
					<RequireAuth access="News">
						<NewsDetail />
					</RequireAuth>
				),
			},
			{
				path: 'venue',
				element: (
					<RequireAuth access="Venue">
						<VenueSummary />
					</RequireAuth>
				),
				handle: { crumb: 'Venue', isClickable: false },
			},
			{
				path: 'venue/add',
				element: (
					<RequireAuth access="Venue">
						<VenueDetail />
					</RequireAuth>
				),
			},
			{
				path: 'venue/edit/:venueId',
				element: (
					<RequireAuth access="Venue">
						<VenueDetail />
					</RequireAuth>
				),
			},
			{
				path: 'programs',
				element: (
					<RequireAuth access="Program">
						<ProgramSummary />
					</RequireAuth>
				),
				handle: { crumb: 'Programs', isClickable: false },
			},
			{
				path: 'programs/add',
				element: (
					<RequireAuth access="Program">
						<ProgramDetail />
					</RequireAuth>
				),
			},
			{
				path: 'programs/edit/:programId',
				element: (
					<RequireAuth access="Program">
						<ProgramDetail />
					</RequireAuth>
				),
			},
			{
				path: 'coupon',
				element: (
					<RequireAuth access="Coupon">
						<Coupon />
					</RequireAuth>
				),
				handle: { crumb: 'Coupon' },
			},
			{
				path: 'calendar',
				element: (
					<RequireAuth access="Operational Calendar">
						<Outlet />
					</RequireAuth>
				),
				handle: { crumb: 'Operational Calendar', isClickable: false },
				children: [
					{
						path: 'closed-schedule',
						element: <ClosedSchedule />,
						handle: { crumb: 'Closed Schedule', isClickable: true },
					},
					{
						path: 'holiday',
						element: <Holiday />,
						handle: { crumb: 'Holiday', isClickable: true },
					},
					{
						path: 'closed-schedule/add',
						element: <ClosedScheduleForm />,
					},
					{
						path: 'closed-schedule/edit/:scheduleId',
						element: <ClosedScheduleForm />,
					},
				],
			},
			{
				path: 'customer',
				element: (
					<RequireAuth access="Customer">
						<Outlet />
					</RequireAuth>
				),
				handle: { crumb: 'Customers' , isClickable: false},
				children: [
					{
						path: 'list',
						element: <Customer />,
						handle: { crumb: 'List Customers', isClickable: true },
					},
					{
						path: 'clubs',
						element: <Clubs />,
						handle: { crumb: 'Clubs', isClickable: true },
					},
					{
						path: 'clubs/edit/:clubId',
						element: <ClubDetail />,
						handle: { crumb: 'Club', isClickable: true },
					}
				]
			},
			{
				path: 'admin',
				element: <Outlet />,
				handle: { crumb: 'User Management', isClickable: false },
				children: [
					{
						path: 'user',
						element: (
							<RequireAuth access="User">
								<User />
							</RequireAuth>
						),
						handle: { crumb: 'User' },
					},
					{
						path: 'role',
						element: (
							<RequireAuth access="Role">
								<Role />
							</RequireAuth>
						),
						handle: { crumb: 'User' },
					},
				],
			},
			{
				path: 'refund',
				element: (
					<RequireAuth access="Refund">
						<Refund />
					</RequireAuth>
				),
				handle: { crumb: 'Refund', isClickable: false },
			},
			{
				path: 'subscriptions',
				element: (
					<RequireAuth access="Subscription">
						<Subscription />
					</RequireAuth>
				),
				handle: { crumb: 'Subscription', isClickable: false },
			},
			{
				path: 'subscriptions/add',
				element: (
					<RequireAuth access="Subscription">
						<SubscriptionDetail />
					</RequireAuth>
				),
			},
			{
				path: 'subscriptions/edit/:id',
				element: (
					<RequireAuth access="Subscription">
						<SubscriptionEdit />
					</RequireAuth>
				),
			},
			{
				path: 'invoices',
				element: (
					<RequireAuth access="Invoices">
						<Invoice />
					</RequireAuth>
				),
				handle: { crumb: 'Invoices', isClickable: false },
			},
			{
				path: 'invoices/:invoiceId',
				element: (
					<RequireAuth access="Invoices">
						<InvoiceDetail />
					</RequireAuth>
				),
				handle: { crumb: 'Invoice Detail', isClickable: false },
			},
			{
				path: 'report',
				element: (
					<RequireAuth access="Report">
						<Outlet />
					</RequireAuth>
				),
				handle: { crumb: 'Report', isClickable: false },
				children: [
					{
						path: 'income',
						element: <IncomeStatement />,
						handle: { crumb: 'Income Statement', isClickable: true },
					},
					{
						path: 'booking',
						element: <BookingInsight />,
						handle: { crumb: 'Booking Insight', isClickable: true },
					},
				],
			},
			{
				path: 'gallery',
				element: (
					<RequireAuth access="Gallery">
						<Outlet />
					</RequireAuth>
				),
				handle: { crumb: 'Gallery', isClickable: false },
				children: [
					{
						path: 'banners',
						element: <Banners />,
						handle: { crumb: 'Banners', isClickable: true },
					},
					{
						path: 'photos',
						element: <Photos />,
						handle: { crumb: 'Photos', isClickable: true },
					},
				],
			},
		],
	},
	{
		path: '/not-authorized',
		element: <div>Not Authorized</div>,
	},
	{
		path: '/customer/refund-info',
		element: <CustomerRefund />
	},
	{
		path: '*',
		element: <div>404 NOT FOUND</div>,
	},
]);

export default router;
